import { useState, useEffect } from "react";
import { IconButton } from "@fluentui/react";
import { useTranslation } from "react-i18next";
import { supportedLngs } from "../../i18n/config";

interface Props {
    answer: string;
    index?: number;
    sessionId?: string; // Add sessionId to differentiate between different chat sessions
}

const SpeechSynthesis = (window as any).speechSynthesis || (window as any).webkitSpeechSynthesis;

let synth: SpeechSynthesis | null = null;
let currentPlayingId: string | null = null; // Track which answer is currently playing (sessionId-index)
let stateUpdateCallbacks: Set<() => void> = new Set(); // Notify all components when playing state changes

// Store selected voice per language (persists across components)
const selectedVoiceByLanguage: { [key: string]: string | null } = {};

// Preferred voices per language - ordered by preference (best first)
// Voice names from Microsoft Azure Speech Service documentation
// Browser voices use format: "Microsoft [Name] Online (Natural) - Language (Region)"
const preferredVoices: { [key: string]: string[] } = {
    "de-DE": [
        "Seraphina",                                                 // Matches de-DE-SeraphinaMultilingualNeural
        "Microsoft Seraphina Online (Natural) - German (Germany)",  // Edge
        "Google Deutsch",                                            // Chrome
        "Anna",                                                      // Safari (macOS)
        "Markus",                                                    // Safari (macOS) male
        "Microsoft Katja Online (Natural) - German (Germany)",      // Edge alternative
        "Microsoft Conrad Online (Natural) - German (Germany)",     // Edge male
    ],
    "en-US": [
        "Ava",                                                       // Matches en-US-AvaMultilingualNeural
        "Microsoft Ava Online (Natural) - English (United States)", // Edge
        "Google US English",                                          // Chrome
        "Samantha",                                                  // Safari (macOS)
        "Alex",                                                      // Safari (macOS) male
        "Victoria",                                                  // Safari (macOS) alternative
        "Microsoft Aria Online (Natural) - English (United States)",
        "Microsoft Jenny Online (Natural) - English (United States)",
    ],
    "es-ES": [
        "Vivienne",                                                   // Multilingual voice that supports Spanish
        "Arabella",                                                   // Matches es-ES-ArabellaMultilingualNeural  
        "Microsoft Elvira Online (Natural) - Spanish (Spain)",       // Edge
        "Google español",                                             // Chrome
        "Monica",                                                     // Safari (macOS)
        "Jorge",                                                      // Safari (macOS) male
        "Microsoft Helena - Spanish (Spain)",                        // Windows built-in
    ],
    "fr-FR": [
        "Vivienne",                                                   // Matches fr-FR-VivienneMultilingualNeural
        "Microsoft Vivienne Online (Natural) - French (France)",     // Edge
        "Microsoft Denise Online (Natural) - French (France)",       // Edge
        "Google français",                                            // Chrome
        "Amelie",                                                     // Safari (macOS)
        "Thomas",                                                     // Safari (macOS) male
        "Microsoft Julie - French (France)",                         // Windows built-in
    ],
    "ja-JP": [
        "Nanami",                                                     // Matches ja-JP-NanamiNeural
        "Microsoft Nanami Online (Natural) - Japanese (Japan)",      // Edge
        "Google 日本語",                                              // Chrome
        "Kyoko",                                                      // Safari (macOS)
        "Otoya",                                                      // Safari (macOS) male
        "Microsoft Ayumi - Japanese (Japan)",                        // Windows built-in
        "Microsoft Haruka - Japanese (Japan)",                       // Windows built-in
    ]
};

try {
    synth = SpeechSynthesis;
} catch (err) {
    console.error("SpeechSynthesis is not supported");
}

// Find the best available voice for a language
const findBestVoice = (voices: SpeechSynthesisVoice[], lngCode: string): SpeechSynthesisVoice | undefined => {
    const preferred = preferredVoices[lngCode] || [];
    
    // Try to find a preferred voice (check partial matches for flexibility)
    for (const prefVoiceName of preferred) {
        const voice = voices.find((v: SpeechSynthesisVoice) => 
            v.name.toLowerCase().includes(prefVoiceName.toLowerCase()) ||
            prefVoiceName.toLowerCase().includes(v.name.toLowerCase())
        );
        if (voice) return voice;
    }
    
    // Fallback: find any voice matching the language
    const langVoice = voices.find((v: SpeechSynthesisVoice) => v.lang === lngCode);
    if (langVoice) return langVoice;
    
    // Fallback: find any voice with same language prefix (e.g., "de" for "de-DE")
    const langPrefix = lngCode.split('-')[0];
    return voices.find((v: SpeechSynthesisVoice) => v.lang.startsWith(langPrefix));
};

const getUtterance = function (text: string, lngCode: string = "en-US", voiceName?: string | null) {
    if (synth) {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lngCode;
        utterance.volume = 1;
        utterance.rate = 1;
        utterance.pitch = 1;

        const voices = synth.getVoices();
        let voice: SpeechSynthesisVoice | undefined;
        
        // If a specific voice is selected, use it
        if (voiceName) {
            voice = voices.find((v: SpeechSynthesisVoice) => v.name === voiceName);
        }
        
        // Use the best available voice for the language
        if (!voice) {
            voice = findBestVoice(voices, lngCode);
        }
        
        // Final fallback to English
        if (!voice) {
            voice = findBestVoice(voices, "en-US") || voices[0];
        }

        utterance.voice = voice;
        return utterance;
    }
};

export const SpeechOutputBrowser = ({ answer, index = 0, sessionId = "default" }: Props) => {
    const { t, i18n } = useTranslation();
    const currentLng = i18n.language;
    let lngCode = supportedLngs[currentLng]?.locale;
    if (!lngCode) {
        lngCode = "en-US";
    }
    const [, forceUpdate] = useState({});
    const [currentVoice, setCurrentVoice] = useState<string | null>(selectedVoiceByLanguage[lngCode] || null);
    
    // Create unique ID for this answer combining sessionId and index
    const uniqueId = `${sessionId}-${index}`;

    // Load available voices and auto-select best voice for language
    useEffect(() => {
        const loadVoices = () => {
            if (synth) {
                const availableVoices = synth.getVoices();
                
                // Auto-select best voice if none selected for this language
                if (!selectedVoiceByLanguage[lngCode] && availableVoices.length > 0) {
                    const bestVoice = findBestVoice(availableVoices, lngCode);
                    if (bestVoice) {
                        selectedVoiceByLanguage[lngCode] = bestVoice.name;
                        setCurrentVoice(bestVoice.name);
                    }
                } else if (selectedVoiceByLanguage[lngCode]) {
                    // Use stored voice for this language
                    setCurrentVoice(selectedVoiceByLanguage[lngCode]);
                }
            }
        };

        loadVoices();
        
        // Chrome loads voices asynchronously
        if (synth) {
            synth.onvoiceschanged = loadVoices;
        }
    }, [lngCode]);

    // Subscribe to global playing state changes
    useEffect(() => {
        const callback = () => forceUpdate({});
        stateUpdateCallbacks.add(callback);
        
        return () => {
            stateUpdateCallbacks.delete(callback);
        };
    }, []);

    // Cleanup effect: Stop speech when component unmounts or page unloads
    useEffect(() => {
        // Stop speech on component unmount
        return () => {
            if (synth && currentPlayingId === uniqueId) {
                synth.cancel();
                currentPlayingId = null;
                stateUpdateCallbacks.forEach(cb => cb());
            }
        };
    }, [uniqueId]);

    // Effect to handle page reload/navigation
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (synth) {
                synth.cancel();
                currentPlayingId = null;
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, []);

    const startOrStopSpeech = (answer: string) => {
        if (synth != null) {
            // If THIS component is playing, stop it
            if (currentPlayingId === uniqueId) {
                synth.cancel();
                currentPlayingId = null;
                stateUpdateCallbacks.forEach(cb => cb());
                return;
            }
            
            // If another component is playing, cancel it first
            if (synth.speaking) {
                synth.cancel();
            }
            
            const utterance: SpeechSynthesisUtterance | undefined = getUtterance(answer, lngCode, currentVoice);

            if (!utterance) {
                return;
            }

            // Set this as the currently playing ID
            currentPlayingId = uniqueId;
            stateUpdateCallbacks.forEach(cb => cb());

            synth.speak(utterance);

            utterance.onstart = () => {
                currentPlayingId = uniqueId;
                stateUpdateCallbacks.forEach(cb => cb());
            };

            utterance.onend = () => {
                if (currentPlayingId === uniqueId) {
                    currentPlayingId = null;
                    stateUpdateCallbacks.forEach(cb => cb());
                }
            };
        }
    };
    
    const isPlaying = currentPlayingId === uniqueId;
    const color = isPlaying ? "red" : "black";

    return (
        <IconButton
            style={{ color: color }}
            iconProps={{ iconName: "Volume3" }}
            title={t("tooltips.speakAnswer")}
            ariaLabel={t("tooltips.speakAnswer")}
            onClick={() => startOrStopSpeech(answer)}
            disabled={!synth}
        />
    );
};
